import { useMemo } from 'react';
import { UseDataQueryResult, useDataQueryResult } from '@/plugin-system/runtime/DataQueriesProvider';
import { VizContainerRootBaseProps } from '../root';

export interface UseVizContainerDataQueriesLoadingProps {
  /**
   * If true, the container is loading for the first time, displays a linear progress bar at the top, and hides the content.
   *
   * This prop supports three different ways of setting the loading state:
   * - `undefined` (default behavior): Loading state is determined by state from `useDataQueryResult`. Useful in simple
   *    cases where the loading state from a `DataQueriesProvider` does everything you need.
   * - `boolean`: Explicitly sets the loading state. Useful in cases where you are not relying on loading state from
   *   a `DataQueriesProvider`.
   * - `function`: Callback function that provides the default loading state as an argument and returns a boolean that
   *   will be used as the loading state. This is useful in mixed cases where you want to combine the loading state
   *   from a `DataQueriesProvider` with some additional custom logic.
   */
  isLoading?:
    | VizContainerRootBaseProps['isLoading']
    | ((isLoading: UseDataQueryResult['isLoading']) => VizContainerRootBaseProps['isLoading']);

  /**
   * If true, the container is fetching new data and displays a linear progress bar at the top, without hiding the content.
   *
   * This prop supports three different ways of setting the fetching state:
   * - `undefined` (default behavior): Fetching state is determined by state from `useDataQueryResult`. Useful in simple
   *    cases where the fetching state from a `DataQueriesProvider` does everything you need.
   * - `boolean`: Explicitly sets the fetching state. Useful in cases where you are not relying on fetching state from
   *   a `DataQueriesProvider`.
   * - `function`: Callback function that provides the default fetching state as an argument and returns a boolean that
   *   will be used as the fetching state. This is useful in mixed cases where you want to combine the fetching state
   *   from a `DataQueriesProvider` with some additional custom logic.
   */
  isFetching?:
    | VizContainerRootBaseProps['isFetching']
    | ((isFetching: UseDataQueryResult['isFetching']) => VizContainerRootBaseProps['isFetching']);
}

export interface UseVizContainerDataQueriesLoadingResponse
  extends Pick<VizContainerRootBaseProps, 'isFetching' | 'isLoading'> {}

/**
 * Hook that helps consistently set the `isLoading` and `isFetching` states on `VizContainer.RootBase`.
 *
 * See documentation on the `isLoading` and `isFetching` props for information about how to customize the state. When
 * these props are not set, the default behavior is to get state from `useDataQueryResult`.
 */
export const useVizContainerDataQueriesLoading = ({
  isLoading: isLoadingProp,
  isFetching: isFetchingProp,
}: UseVizContainerDataQueriesLoadingProps = {}): UseVizContainerDataQueriesLoadingResponse => {
  const {
    isFetching: isFetchingData,
    isFetchingMore: isFetchingMoreData,
    isLoading: isLoadingData,
  } = useDataQueryResult();

  const isLoading = useMemo(() => {
    const defaultIsLoading = isLoadingData;

    if (typeof isLoadingProp === 'function') {
      return isLoadingProp(defaultIsLoading);
    }

    if (typeof isLoadingProp === 'boolean') {
      return isLoadingProp;
    }

    return defaultIsLoading;
  }, [isLoadingData, isLoadingProp]);

  const isFetching = useMemo(() => {
    const defaultIsFetching = isFetchingData || isFetchingMoreData;

    if (typeof isFetchingProp === 'function') {
      return isFetchingProp(defaultIsFetching);
    }

    if (typeof isFetchingProp === 'boolean') {
      return isFetchingProp;
    }

    return defaultIsFetching;
  }, [isFetchingData, isFetchingMoreData, isFetchingProp]);

  return useMemo(() => {
    return {
      isLoading,
      isFetching,
    };
  }, [isFetching, isLoading]);
};
